'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Mail, Lock, Eye, EyeOff, User, Building2,
  Smartphone, Loader2, ChevronDown, FileText,
} from 'lucide-react';
import { toast } from 'sonner';

interface RegisterFormData {
  fullName: string;
  companyName: string;
  email: string;
  mobile: string;
  role: string;
  gstin: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

const ROLES = [
  'Importer',
  'Exporter',
  'Freight Forwarder',
  'Customs Broker',
  'Logistics Manager',
  'Shipping Agent',
  'Other',
];

export default function RegisterForm({ onSwitchToLogin }: { onSwitchToLogin: () => void }) {
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>();

  const password = watch('password');

  const onSubmit = async (data: RegisterFormData) => {
    // Backend integration point: POST /api/auth/register with all fields
    await new Promise((r) => setTimeout(r, 1400));
    toast.success('Account created! Welcome to EzyTrack.');
    window.location.href = '/my-alerts';
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Create account</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Start tracking your shipments for free
        </p>
      </div>

      {/* Full name */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">
          Full Name <span className="text-danger">*</span>
        </label>
        <div className="relative">
          <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            placeholder="Rahul Sharma"
            {...register('fullName', { required: 'Full name is required.' })}
            className="input-field w-full h-11 pl-9 pr-4 text-sm"
          />
        </div>
        {errors.fullName && <p className="text-xs text-danger mt-1">{errors.fullName.message}</p>}
      </div>

      {/* Company */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">
          Company Name <span className="text-danger">*</span>
        </label>
        <div className="relative">
          <Building2 size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            placeholder="Indo Exim Pvt. Ltd."
            {...register('companyName', { required: 'Company name is required.' })}
            className="input-field w-full h-11 pl-9 pr-4 text-sm"
          />
        </div>
        {errors.companyName && <p className="text-xs text-danger mt-1">{errors.companyName.message}</p>}
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">
          Email Address <span className="text-danger">*</span>
        </label>
        <div className="relative">
          <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type="email"
            placeholder="rahul@indoexim.co.in"
            {...register('email', {
              required: 'Email is required.',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email.' },
            })}
            className="input-field w-full h-11 pl-9 pr-4 text-sm"
          />
        </div>
        {errors.email && <p className="text-xs text-danger mt-1">{errors.email.message}</p>}
      </div>

      {/* Mobile */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">
          Mobile Number <span className="text-danger">*</span>
        </label>
        <p className="text-xs text-muted-foreground mb-1.5">Used for SMS shipment alerts</p>
        <div className="flex gap-2">
          <div className="h-11 px-3 flex items-center text-sm font-semibold text-foreground bg-muted border border-border rounded-lg shrink-0">
            +91
          </div>
          <div className="relative flex-1">
            <Smartphone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="tel"
              placeholder="98765 43210"
              {...register('mobile', {
                required: 'Mobile number is required.',
                pattern: { value: /^[6-9]\d{9}$/, message: 'Enter a valid 10-digit Indian mobile number.' },
              })}
              className="input-field w-full h-11 pl-9 pr-4 text-sm"
            />
          </div>
        </div>
        {errors.mobile && <p className="text-xs text-danger mt-1">{errors.mobile.message}</p>}
      </div>

      {/* Role */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">
          Your Role <span className="text-danger">*</span>
        </label>
        <div className="relative">
          <ChevronDown size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <select
            {...register('role', { required: 'Please select your role.' })}
            className="input-field w-full h-11 pl-4 pr-9 text-sm appearance-none cursor-pointer"
          >
            <option value="">Select your role...</option>
            {ROLES.map((r) => (
              <option key={`role-${r}`} value={r}>{r}</option>
            ))}
          </select>
        </div>
        {errors.role && <p className="text-xs text-danger mt-1">{errors.role.message}</p>}
      </div>

      {/* GSTIN */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">
          GSTIN <span className="text-muted-foreground text-xs font-normal">(Optional)</span>
        </label>
        <div className="relative">
          <FileText size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            placeholder="27AABCU9603R1ZX"
            {...register('gstin', {
              pattern: { value: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, message: 'Enter a valid GSTIN format.' },
            })}
            className="input-field w-full h-11 pl-9 pr-4 text-sm uppercase"
          />
        </div>
        {errors.gstin && <p className="text-xs text-danger mt-1">{errors.gstin.message}</p>}
      </div>

      {/* Password */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">
          Password <span className="text-danger">*</span>
        </label>
        <p className="text-xs text-muted-foreground mb-1.5">Minimum 8 characters with at least one number</p>
        <div className="relative">
          <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type={showPass ? 'text' : 'password'}
            placeholder="Create a strong password"
            {...register('password', {
              required: 'Password is required.',
              minLength: { value: 8, message: 'Password must be at least 8 characters.' },
              pattern: { value: /(?=.*\d)/, message: 'Password must contain at least one number.' },
            })}
            className="input-field w-full h-11 pl-9 pr-10 text-sm"
          />
          <button
            type="button"
            onClick={() => setShowPass(!showPass)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
        {errors.password && <p className="text-xs text-danger mt-1">{errors.password.message}</p>}
      </div>

      {/* Confirm password */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">
          Confirm Password <span className="text-danger">*</span>
        </label>
        <div className="relative">
          <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type={showConfirm ? 'text' : 'password'}
            placeholder="Repeat your password"
            {...register('confirmPassword', {
              required: 'Please confirm your password.',
              validate: (val) => val === password || 'Passwords do not match.',
            })}
            className="input-field w-full h-11 pl-9 pr-10 text-sm"
          />
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
        {errors.confirmPassword && <p className="text-xs text-danger mt-1">{errors.confirmPassword.message}</p>}
      </div>

      {/* Terms */}
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          {...register('terms', { required: 'You must accept the terms to register.' })}
          className="w-4 h-4 accent-primary rounded mt-0.5 shrink-0"
        />
        <span className="text-xs text-muted-foreground leading-relaxed">
          I agree to the{' '}
          <span className="text-accent font-semibold hover:text-primary cursor-pointer">Terms of Service</span>
          {' '}and{' '}
          <span className="text-accent font-semibold hover:text-primary cursor-pointer">Privacy Policy</span>
        </span>
      </label>
      {errors.terms && <p className="text-xs text-danger -mt-2">{errors.terms.message}</p>}

      {/* Submit */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="btn-primary h-11 flex items-center justify-center gap-2 text-sm mt-1"
      >
        {isSubmitting ? (
          <><Loader2 size={16} className="animate-spin" /> Creating Account...</>
        ) : (
          'Create Free Account'
        )}
      </button>

      <p className="text-sm text-center text-muted-foreground">
        Already have an account?{' '}
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="text-accent font-semibold hover:text-primary transition-colors"
        >
          Sign in
        </button>
      </p>
    </form>
  );
}